import { IS_BROWSER_MAIN_THREAD } from "../consts.js";

export class AppProcessor {
  constructor(
    storage,
    pipeService,
    req,
    websocket,
    util,
    activateSW,
    dataService,
  ) {
    this.storage = storage;
    this.pipeService = pipeService;
    this.req = req;
    this.ws = websocket;
    this.applistReceived = false;
    this.activateSW = activateSW; // we use this flag to check if applist was received. The problem is that if you receive an app it also goes to app_list as a result you have the app_list with one app.
    this.util = util;
    this.appListeners();
    this.dataService = dataService;

    this.appRequestCache = new Map;
  }

  async createNewAppApi(app) {
    try {
      const response = await this.req.post({
        url: "/api/app/create",
        form: { app: JSON.stringify(app) },
      });
      response.from_apps_list = true;
      return response;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async updateAppApi(app) {
    try {
      const response = await this.req.post({
        url: "/api/app/update",
        form: { app: JSON.stringify(app) },
      });
      return response;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async deleteAppApi(app_id) {
    try {
      const response = await this.req.post({
        url: "/api/app/delete",
        form: { app_id },
      });
      return response;
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  async getAppListApi() {
    try {
      const response = await this.req.get({
        url: "/api/applist/get",
      });
      return response.apps_list.map((app) => {
        app.from_apps_list = true;
        return app;
      });
    } catch (err) {
      console.log(err);
      return null;
    }
  }

  getAppListFromStorage() {
    return this.storage.getAppsList();
  }

  getAppFromStorage(app_id) {
    const app = this.storage.getApp(app_id);
    return app && app.field_list.length ? app : null;
  }

  addNewAppToStorage(app) {
    return this.storage.getAppsList().push(app);
  }

  saveAppInStorage(app) {

    app.items_object = {};

    for(let item = 0; item < app.items_list.length; item++) {
      app.items_object[app.items_list[item].item_id] = {};
        for(let field = 0; field < app.items_list[item].fields.length; field++) {
          app.items_object[app.items_list[item].item_id][app.items_list[item].fields[field].field_id] = app.items_list[item].fields[field];
        }
    }

    const storageApp = this.storage.getApp(app.app_id);
    if (storageApp) {
      app.from_apps_list = storageApp.from_apps_list;
      app.permission = storageApp.permission;
      this.storage.updateApp(app);
    } else {
      app.from_apps_list = false;
      this.addNewAppToStorage(app);
    }
    return this.getAppFromStorage(app.app_id);
  }

  updateAppFieldsAndViews(app) {
    const storageApp = this.getAppFromStorage(app.app_id);
    app.items_list = storageApp.items_list;
    app.file_list = storageApp.file_list;
    app.from_apps_list = storageApp.from_apps_list;
    app.items_object  =storageApp.items_object;
    this.storage.updateApp(app);
    //-- Sending updates for Views Updates
    this.pipeService.emit(
      "gh_app_views_update",
      { app_id: app.app_id },
      app.views_list
    );

    //-- Sending updates for updating Fields
    app.field_list.forEach((fieldModel) => {
      this.pipeService.emit(
        "gh_model_update",
        { app_id: app.app_id, field_id: fieldModel.field_id },
        fieldModel
      );
    });
  }

  updateAppInStorage(app) {
    const storageApp = this.storage.getApp(app.app_id);
    if (storageApp) {
      this.storage.updateApp(app);
      this.pipeService.emit(
        "gh_app_update",
        { app_id: app.app_id },
        app
      );
      this.pipeService.emit(
        "gh_items_update",
        { app_id: app.app_id },
        app.items_list
      );
    } else { 
      this.addNewAppToStorage(app) 
    };
  }

  deletingAppFromStorage(app_id) {
    let appList = this.storage.getAppsList();
    appList.forEach((app, key) => {
      if (app.app_id == app_id) appList.splice(key);
    });

    return appList;
  }

  //-- here we check if app already in storage then we dont update it.
  //-- the thing is that is posible that we receive app before receiveng an applist
  updateAppsListInStorage(appsList = []) {
    for (const app of appsList) {
      const storageApp = this.storage.getApp(app.app_id);
      if (storageApp) {
        storageApp.from_apps_list = app.from_apps_list;
        storageApp.permission = app.permission;
      } else this.addNewAppToStorage(app);
    }
  }

  async refreshApps(apps_id = []) {
    for (const app_id of apps_id) {
      if (app_id != undefined) {
        try {
          const app = await this.dataService.getApp(app_id);
          if (app) {
            app.items_object = {};

            for(let item = 0; item < app.items_list.length; item++) {
              app.items_object[app.items_list[item].item_id] = {};
                for(let field = 0; field < app.items_list[item].fields.length; field++) {
                  app.items_object[app.items_list[item].item_id][app.items_list[item].fields[field].field_id] = app.items_list[item].fields[field];
                }
            }
            this.updateAppInStorage(app);
            // This emit needs to update values in app after websockets reconnect
            this.pipeService.emit('gh_app_views_update', { app_id: app.app_id }, app.views_list);
          }
        } catch (err) {
          console.log(err);
        }
      }
    }
    console.log("Apps refreshed: ", JSON.stringify(apps_id));
  }

  async refreshAppsList() {
    let currentAppsList = this.getAppListFromStorage();
    let appsListFromApi = await this.getAppListApi();
    let mergedAppsList = appsListFromApi.map(apiApp => {
      return { ...apiApp, ...currentAppsList.find(app => app.app_id === apiApp.app_id) }
    })

    this.updateAppsListInStorage(mergedAppsList);

    this.pipeService.emit('gh_apps_list_refreshed', {});
  }

  async getAppsList() {
    let app_list = this.getAppListFromStorage();
    if (!this.applistReceived) {
      let received_app_list = await this.getAppListApi();
      if (!received_app_list) return null;

      // for oprimization purpose: in case when there was severals calls at same moment of the getAppLists one call will overwrite app_list of another call with the same app_list,
      this.updateAppsListInStorage(received_app_list);
      this.applistReceived = true;
      app_list = received_app_list;
      
      // Here we get default user's app
      // We do it here to do it only once
      // We do it because we need to initialize WebSockets, which we do in getApp method
      // this.getApp(this.storage.getUser().app_init); // commented due to unfinished app_init development, its shape causes exceptions
    }

    return app_list;
  }

  //********** GET APP INFO ************/
  //-- it returs App with Empty fields_list, items_list, file_list, views_list
  //-- Such simmple data needed to render App Icons, and do some simple updates in App like updating App name icon permisions etc when full app data is not needed
  async getAppInfo(app_id) {
    const app_list = await this.getAppsList();
    return app_list ? app_list.find((app) => app.app_id == app_id) : null;
  }

  async deleteApp(app_id) {
    await this.deleteAppApi(app_id);
    return this.deletingAppFromStorage(app_id);
  }

  async getApp(app_id, trash = false) {
    if (!app_id) return null;

    let app = this.getAppFromStorage(app_id);
    if (app) return app;

    if (this.appRequestCache.has(app_id)) return this.appRequestCache.get(app_id);

    let self = this;

    let pr = new Promise(async (resolve, reject) => {
      try {
        let app = await self.dataService.getApp(app_id);
        if (!app) reject();
    
        //!!! temporary check !!!! if app has chanks property then we start getting it
        // if (
        //   receivedApp.chunks &&
        //   receivedApp.chunks.length
        // ) {
        //   // here should be checked is merge required


        //   let chunks = await self.chunksManager.getChunks(app_id, receivedApp.chunks);
        //   receivedApp.items_list = self.util.mergeChunks([
        //     ...chunks,
        //     receivedApp,
        //   ]);
        // }


        // This code for trash must be changed
        // return trash ? app : {...app, items_list: app.items_list.filter(item => !item.trash)};
        let filtered =  trash ? app : {...app, items_list: app.items_list.filter(item => !item.trash)};
        filtered =  trash ? filtered : {...filtered, field_list: filtered.field_list.filter(f => !f.trash)};

        resolve(filtered);
    
        self.saveAppInStorage(filtered);
        self.ws.addSubscription(app_id);
      } catch (error) {
        reject();
      }
    });

    self.appRequestCache.set(app_id, pr);
  
    return pr;
  }

  async updateApp(app) {
    if (!app.views_list || !app.views_list.length || !app.show) {
      const storageApp = await this.getApp(app.app_id);

      if(!app.views_list || !app.views_list.length) {
        app.views_list = storageApp.views_list;
      }

      if(typeof app.show === 'undefined') {
        app.show = storageApp.show;
      }
    }

    const updatedApp = await this.updateAppApi(app);
    this.updateAppFieldsAndViews(updatedApp);
    return updatedApp;
  }

  async updateAppInfo(appInfo = {}) {
    this.pipeService.emit(
      "gh_app_info_update",
      { app_id: appInfo.app_id },
      appInfo
    );
    return this.updateApp(appInfo);
  }

  async createNewApp(app) {
    const newApp = await this.createNewAppApi(app);
    newApp.items_object = {};
    this.addNewAppToStorage(newApp);
    return newApp;
  }

  async getAppViews(app_id) {
    if(app_id) {
      const app = await this.getApp(app_id);
      return app.views_list;
    }
  }

  async handleAppChange(id, prevVersion, nextVersion) {
    let res1 = this.util.areViewListsEqual(prevVersion.views_list, nextVersion.views_list);
    let res2 = this.util.areFieldListsEqual(prevVersion.field_list, nextVersion.field_list);

    if (
      res1 ||
      res2
    ) {
      this.updateAppFieldsAndViews({id, views_list: nextVersion.views_list, field_list: nextVersion.field_list});
    }
  }

  clearAppProcessor() {
    this.getAppListPromises = null;
    this.getAppPromises = {};
    this.applistReceived = false;
  }

  appListeners() {
    this.pipeService.onRoot("gh_app_get", {}, async (event, data) => {
      if (data && data.app_id) {
        const app = await this.getApp(data.app_id);
        this.pipeService.emit("gh_app_get", data, app);
      }
    });

    this.pipeService.onRoot("gh_apps_list_get", {}, async (event, data) => {
      const appList = await this.getAppsList();
      this.pipeService.emit("gh_apps_list_get", data, appList);
    });

    this.pipeService.onRoot("gh_delete_app", {}, async (event, data) => {
      const appList = await this.deleteApp(data.app_id);
      this.pipeService.emit(
        "gh_apps_list_update",
        { recipient: "all" },
        appList
      );
    });

    this.pipeService.onRoot("gh_app_update", {}, async (event, data) => {
      data.app.items_list = [];
      data.app.file_list = [];

      const newApp = await this.updateApp(data.app);

      this.pipeService.emit(
        "gh_app_views_update",
        { app_id: newApp.app_id },
        newApp.views_list
      );

      const appsList = await this.getAppsList();
      this.pipeService.emit(
        "gh_apps_list_update",
        { recipient: "all" },
        appsList
      );

      newApp.field_list.forEach((fieldModel) => {
        this.pipeService.emit(
          "gh_model_update",
          {
            app_id: newApp.app_id,
            field_id: fieldModel.field_id,
          },
          fieldModel
        );
      });
    });

    this.pipeService.onRoot("gh_app_view_get", {}, async (event, data) => {
      if (data && data.app_id) {
        const app = await this.getApp(data.app_id);
        app.views_list.forEach((view, key) => {
          if (view.view_id == data.view_id) {
            this.pipeService.emit("gh_app_view_get", data, app.views_list[key]);
          }
        });
      }
    });

    // Return app_name, app_id, app_icon
    this.pipeService.onRoot("gh_app_info_get", {}, async (event, data) => {
      const appInfo = await this.getAppInfo(data.app_id);
      if (appInfo) this.pipeService.emit("gh_app_info_get", data, appInfo);
    });

    this.pipeService.onRoot("gh_app_info_update", {}, async (event, data) => {
      if (data && data.app) {
        const appInfo = await this.updateAppInfo(data.app);
        this.pipeService.emit(
          "gh_app_info_update",
          { app_id: data.app.app_id },
          appInfo
        );
      }
    });

    this.pipeService.onRoot("gh_app_create", {}, async (event, data) => {
      await this.createNewApp(data.app);
      const appsList = await this.getAppsList();
      this.pipeService.emit(
        "gh_apps_list_update",
        { recipient: "all" },
        appsList
      );
    });
    if (
      IS_BROWSER_MAIN_THREAD &&
      this.activateSW
    ) {
      navigator.serviceWorker.addEventListener("message", async (event) => {
        if (event.data.type === "refresh app") {
          const app = await this.getApp(event.data.payload.app_id);
          if (app) {
            this.util.compareAppsItemsLists(
              app.items_list,
              event.data.payload.items_list.filter(item => !item.trash),
              ({ diff_fields_items, diff_fields_items_Ids, diff_items, newItems, deletedItems }) => {
                if (diff_items.length || newItems.length || deletedItems.length) {
               
                  this.pipeService.emit(
                    "gh_items_update",
                    { app_id: event.data.payload.app_id },
                    event.data.payload.items_list.filter(item => !item.trash),
                  );
                  diff_items.forEach((item) =>
                    this.pipeService.emit(
                      "gh_item_update",
                      { app_id: event.data.payload.app_id, item_id: item.item_id },
                      item
                    )
                  );
                }
                diff_fields_items_Ids.forEach((item_id) => {
                  const item = diff_fields_items[item_id];
                  if (!item) return;
                  item.forEach((field) => {
                    this.pipeService.emit(
                      "gh_value_update",
                      {
                        app_id: event.data.payload.app_id,
                        item_id,
                        field_id: field.field_id,
                      },
                      field.field_value
                    );
                  });
                });
              }
            );
          }
          event.data.payload.items_list = await this.util.mergeChunks([app, event.data.payload])
          this.saveAppInStorage(event.data.payload);
        }
        // if (event.data.type === "refresh appList") {
        //   this.storage.unsetApps();
        //   this.updateAppsListInStorage(
        //     event.data.payload.apps_list.map((app) => {
        //       app.from_apps_list = true;
        //       return app;
        //     })
        //   );
        //   this.pipeService.emit(
        //     "gh_apps_list_update",
        //     { recipient: "all" },
        //     event.data.payload.apps_list
        //   );
        // }
      });
    }
  }
}
